import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./Toast.css";

const ToastContext = createContext(null);

const DEFAULT_DURATION = 4000;
const TYPES = new Set(["success", "error", "warning", "info"]);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef(new Map());
  const nextId = useRef(0);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback((message, options = {}) => {
    if (!message) return null;
    const id = `toast-${Date.now()}-${nextId.current++}`;
    const type = TYPES.has(options.type) ? options.type : "info";
    const duration = Number.isFinite(options.duration)
      ? Math.max(options.duration, 0)
      : DEFAULT_DURATION;

    const toast = {
      id,
      message,
      type,
      duration,
      action: options.action || null,
    };

    setToasts((prev) => [...prev, toast]);

    if (duration !== 0 && duration !== Infinity) {
      const timeout = setTimeout(() => removeToast(id), duration);
      timeoutsRef.current.set(id, timeout);
    }

    return id;
  }, [removeToast]);

  useEffect(() => {
    const timeoutsMap = timeoutsRef.current;
    return () => {
      timeoutsMap.forEach((timeout) => clearTimeout(timeout));
      timeoutsMap.clear();
    };
  }, []);

  const contextValue = useMemo(() => ({ showToast, removeToast }), [showToast, removeToast]);

  const portalTarget =
    typeof document !== "undefined"
      ? document.body
      : null;

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {portalTarget &&
        createPortal(
          <div className="toast-viewport" role="status" aria-live="polite" aria-atomic="true">
            {toasts.map((toast) => (
              <div
                key={toast.id}
                className={`toast-item toast-${toast.type}`}
              >
                <span className="toast-message">{toast.message}</span>
                {toast.action && (
                  <button
                    type="button"
                    className="toast-action"
                    onClick={() => {
                      toast.action?.onClick?.();
                      if (!toast.action?.persist) removeToast(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </button>
                )}
                <button
                  type="button"
                  className="toast-dismiss"
                  aria-label="Fechar"
                  onClick={() => removeToast(toast.id)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>,
          portalTarget
        )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}

export default ToastProvider;
